import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useApp } from "@/store/useApp";
import { Trash2, ShoppingBag, Lock, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { QuantityStepper } from "@/components/ui/quantity-stepper";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CartDrawer({ open, onOpenChange }: Props) {
  const { cart, updateQty, removeFromCart, clearCart, account } = useApp();
  const navigate = useNavigate();
  const validated = account?.status === "VALIDADO";

  const subtotal = validated
    ? cart.reduce((acc, i) => acc + (i.pricePerUnit ?? 0) * i.quantity, 0)
    : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0 bg-background">
        <SheetHeader className="px-6 py-5 border-b border-border bg-surface-elevated">
          <SheetTitle className="flex items-center gap-3 font-display text-xl">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-primary text-primary-foreground">
              <ShoppingBag className="h-4 w-4" />
            </span>
            <div className="flex flex-col items-start gap-0">
              <span>Seu Carrinho</span>
              <span className="text-xs font-normal text-muted-foreground">
                {cart.length} {cart.length === 1 ? "item" : "itens"}
              </span>
            </div>
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          {cart.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center px-6">
              <div className="mb-4 h-16 w-16 rounded-full bg-secondary flex items-center justify-center">
                <ShoppingBag className="h-7 w-7 text-muted-foreground" />
              </div>
              <h3 className="font-display text-lg font-semibold">Carrinho vazio</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Adicione itens do catálogo para montar seu pedido.
              </p>
            </div>
          ) : (
            <ul className="flex flex-col gap-2">
              {cart.map((item) => (
                <li
                  key={item.id}
                  className="surface-card p-3 flex flex-col gap-2.5 animate-fade-in"
                >
                  <div className="flex items-start gap-3">
                    <div className="h-12 w-12 rounded-lg bg-secondary flex items-center justify-center text-2xl shrink-0">
                      {item.emoji}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-display font-semibold text-sm leading-tight uppercase tracking-wide">
                        {item.productName}
                      </h4>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                        <span>por {item.unit}</span>
                        {item.cut && (
                          <span className="inline-flex items-center rounded-md bg-primary/10 text-primary px-1.5 py-0.5 font-medium">
                            {item.cut}
                          </span>
                        )}
                      </div>
                      {item.notes && (
                        <p className="mt-1 text-xs text-amber-700">Obs.: {item.notes}</p>
                      )}
                    </div>
                    <button
                      onClick={() => void removeFromCart(item.id)}
                      className="text-muted-foreground hover:text-destructive transition-colors p-1"
                      aria-label="Remover"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <QuantityStepper
                      value={item.quantity}
                      onChange={(next) => void updateQty(item.id, next)}
                    />
                    {validated && item.pricePerUnit ? (
                      <span className="font-display font-semibold tabular-nums">
                        R$ {(item.pricePerUnit * item.quantity).toFixed(2)}
                      </span>
                    ) : validated ? (
                      <span className="text-xs text-muted-foreground inline-flex shrink-0 items-center gap-1 whitespace-nowrap">
                        <span>preço a definir</span>
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground inline-flex shrink-0 items-center gap-1 whitespace-nowrap">
                        <Lock className="h-3 w-3 shrink-0" />
                        <span>sob aprovação</span>
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {cart.length > 0 && (
          <div className="border-t border-border bg-surface-elevated p-5 space-y-3">
            {validated ? (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Subtotal</span>
                <span className="font-display text-2xl font-bold tabular-nums">
                  R$ {subtotal!.toFixed(2)}
                </span>
              </div>
            ) : (
              <div className="rounded-lg border border-warning/30 bg-warning/5 p-3 text-sm">
                <div className="flex items-start gap-2">
                  <Lock className="h-4 w-4 text-warning mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-foreground">Pedido em modo cotação</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Sua conta ainda está em análise. Você pode montar o pedido — preços e fechamento liberam após validação.
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" size="lg" className="w-full" onClick={() => void clearCart()}>
                Limpar carrinho
              </Button>
              <Button
                variant="hero"
                size="lg"
                className="w-full h-auto min-h-12 whitespace-normal text-center leading-tight px-3 text-sm sm:text-base"
                onClick={() => {
                  onOpenChange(false);
                  navigate("/checkout");
                }}
              >
                {validated ? "Finalizar Pedido" : "Revisar e enviar para cotação"}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
